import * as React from 'react';
import { groupPhaseTitleForReleasePhase } from '../helpers/displayHelpers';
import { ReleaseTimelinePhase } from '../types';
import { antGrey, runwayColors } from './styles/globalStyles';
import styles from './styles/timelineStyles';
import Text from 'antd/lib/typography/Text';
import { AppComponentProps } from '../typescriptHelpers';
import { FetchablePaginatedEntityType } from '../entityType';
import Spin from 'antd/lib/spin';
import { globalMessage } from '../utils/globalMessage';
import Button from 'antd/lib/button';
import ReleasesSidebarNode from '@/components/sidebar/ReleasesSidebarNode';
import { selectCompletedReleases, selectCurrentReleases, selectUpcomingReleases } from '@/selectors/memoizedSelectors';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

interface ReleaseGroupProps extends AppComponentProps {
    orgIdParam: string;
    appIdParam: string;
    groupPhase: ReleaseTimelinePhase;
    onClickLoadMore?: () => void;
    totalCount?: number;
}

const ReleaseGroup: React.FC<ReleaseGroupProps> = (props: ReleaseGroupProps) => {
    const groupTitle = groupPhaseTitleForReleasePhase(props.groupPhase);
    const emptyText = emptyTextforGroupPhase(props.groupPhase);
    const showNodeConnectors = props.groupPhase === ReleaseTimelinePhase.Completed;

    const releases = useSelector((state: RootState) => {
        switch (props.groupPhase) {
            case ReleaseTimelinePhase.Completed:
                return selectCompletedReleases(state, props.appId);
            case ReleaseTimelinePhase.Current:
                return selectCurrentReleases(state, props.appId);
            case ReleaseTimelinePhase.Upcoming:
                return selectUpcomingReleases(state, props.appId);
            default:
                return [];
        }
    });

    const fetchCompletedReleasesIsLoading = useIsLoading(FetchablePaginatedEntityType.Releases, props.appId);
    const fetchCompletedReleasesError = useError(FetchablePaginatedEntityType.Releases, props.appId);

    const isCompletedReleasesGroup = props.groupPhase === ReleaseTimelinePhase.Completed;

    const releasesCount = releases.length;

    const showFetchCompletedReleasesIsLoading =
        isCompletedReleasesGroup && fetchCompletedReleasesIsLoading && releasesCount === 0;

    const showFetchMoreCompletedReleasesButton =
        isCompletedReleasesGroup &&
        !showFetchCompletedReleasesIsLoading &&
        props.totalCount !== 0 &&
        props.totalCount !== releasesCount;

    React.useEffect(() => {
        if (fetchCompletedReleasesError && isCompletedReleasesGroup) {
            globalMessage.error(fetchCompletedReleasesError);
        }
    }, [fetchCompletedReleasesError]);

    const noReleases = releasesCount === 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: noReleases ? 8 : 0 }}>
            <Text
                style={{
                    ...styles.timelineContent,
                    textTransform: 'uppercase',
                    fontSize: 10,
                    fontWeight: 600,
                    color: antGrey.grey6,
                    letterSpacing: '0.1em',
                }}
            >
                {groupTitle}
            </Text>
            {showFetchCompletedReleasesIsLoading ?
                <div style={{ height: 50, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spin style={{ fontSize: 15 }} />
                </div>
            :   <>
                    {releasesCount > 0 && (
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                            {releases.map((release, index) => {
                                return (
                                    <li key={release.id}>
                                        <ReleasesSidebarNode
                                            orgId={props.orgId}
                                            appId={props.appId}
                                            releaseId={release.id}
                                            index={index}
                                            releasesCount={releasesCount}
                                            showConnectors={showNodeConnectors}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {noReleases && (
                        <Text type="secondary" style={{ ...styles.timelineContent }}>
                            {emptyText}
                        </Text>
                    )}
                </>
            }
            {showFetchMoreCompletedReleasesButton && (
                <Button
                    type="text"
                    loading={fetchCompletedReleasesIsLoading}
                    onClick={props.onClickLoadMore}
                    style={{ color: runwayColors.primary }}
                >
                    Load more
                </Button>
            )}
        </div>
    );
};

export default ReleaseGroup;

export const emptyTextforGroupPhase = (phase: ReleaseTimelinePhase) => {
    switch (phase) {
        case ReleaseTimelinePhase.Upcoming:
            return 'No upcoming releases.';
        case ReleaseTimelinePhase.Current:
            return 'No next release.';
        case ReleaseTimelinePhase.Completed:
            return 'No completed releases.';
        default:
            return '';
    }
};
